"use client";

import { createContext, FormEvent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import type {
    PageLink,
    PublicShopBasicData,
    ResolvedTemplateDesign,
    ShopPageContext,
    SitePageContent,
    TemplateLinkComponent,
    TemplateLinkProps,
} from "../types";
import { DefaultTemplateLink } from "../templateLink";
import type { ShopStorefrontMessageKey, ShopStorefrontMessageValues } from "../storefrontCatalog";
import { shopStorefrontMessage } from "../storefrontCatalog";
import { hrefForStorefrontLanguage as hrefForLanguage, localizedStorefrontHref, storefrontLanguagePrefix, storefrontSpecialRouteHref, storefrontSpecialRouteMatch } from "../storefrontRouting";
import { SHOP_BUILDER_SCHEMA_VERSION } from "../builderSchema";
import {
    siteBuilderLeadValuesFromFormData,
    submitSiteBuilderLead,
} from "../siteBuilderLeadRuntime";
import { templateLeadStartedAt } from "../templateLeadSubmission";
import { resolveSiteBuilderLeadForm } from "../useSiteBuilderLeadForm";
import type { ReactNode } from "react";
import {
    CATALOG_SALE_RETURN_PARAM,
    CURRENCY_QUERY_PARAM,
    EMPTY_SHOP_CURRENCIES,
    EMPTY_SHOP_LANGUAGES,
    FAVORITE_PRODUCTS_STORAGE_KEY,
    LANGUAGE_QUERY_PARAM,
    activeMenuSection,
    activeMenuUuidsFromContext,
    addRecentlyViewedProduct,
    animationRefreshSnapshot,
    checkoutCompletedOrderStorageKey,
    checkoutFlowHrefs,
    checkoutFormWithCustomerDefaults,
    checkoutRouteStepFromPath,
    customerErrorTextFromResponse,
    emptyCart,
    emptyCheckoutForm,
    emptyCheckoutOptions,
    enrichStoreMenuItems,
    errorTextFromResponse,
    favoriteProductIdsStorageKey,
    formatDate,
    formatMoney,
    hrefForCurrency,
    pageContextPath,
    pageHref,
    parseStoredPublicOrderCreated,
    pathWithCurrency,
    publicOrderApiBasePath,
    readFavoriteProductIds,
    readFavoriteProductPreviewMap,
    readRecentlyViewedProducts,
    safeCatalogReturnHref,
    sectionMenuItems,
    storeCitiesFromContext,
    storefrontLocale,
    withLocalizedStorefrontDefaults,
    withoutProductionDemoContent,
    writeFavoriteProductIds,
    writeFavoriteProductPreviewMap,
} from "./shopStorefrontCore";
import { useReplayAppearanceAnimations } from "./shopStorefrontAnimationRuntime";
import { isLagunaRuntimeRegion, lagunaRuntimeBlock } from "./lagunaBuilder";
import { findShopHeaderRoot } from "./shopHeader";
import { resolveShopRuntimeNavigationItems } from "./shopSiteNavigation";
import { ShopManufacturingApiProvider } from "./ShopManufacturingCustomization";
import type {
    CheckoutFormState,
    CheckoutRouteStep,
    FavoriteProductPreview,
    HeaderSelectorOption,
    MobileTab,
    PickupPointSelection,
    PublicCart,
    PublicCheckoutOptions,
    PublicCustomerAddress,
    PublicCustomerAddressListResponse,
    PublicCustomerProfileUpdate,
    PublicCustomerSession,
    PublicOrderCreated,
    PublicOrderReorderResponse,
    ShopResolvePayload,
    ShopStorefrontFormatting,
    ShopStorefrontPreviewAdapter,
} from "./shopStorefrontCore";
import type { PublicManufacturingSpecification } from "./shopManufacturingCustomization";
import { publicCartItemAddBody, publicCartItemUpdateBody } from "./shopManufacturingCustomization";

export type ShopStorefrontRuntimeProps = {
    siteName: string;
    pages: PageLink[];
    page?: SitePageContent | null;
    pageContext?: ShopPageContext | null;
    shopBasicData?: PublicShopBasicData | null;
    cartApiBasePath?: string;
    customerApiBasePath?: string;
    previewCustomerSession?: PublicCustomerSession;
    onStorefrontLanguageSelect?: (languageCode: string, href: string) => void;
    onStorefrontNavigate?: (href: string) => void;
    storefrontLocationHref?: string;
    shopResolveApiPath?: string;
    shopSearchApiPath?: string;
    manufacturingProfileApiBasePath?: string;
    packetaApiKey?: string;
    LinkComponent?: TemplateLinkComponent;
    design: ResolvedTemplateDesign;
    editorMode: boolean;
};

export type ShopStorefrontRuntimeHookProps = ShopStorefrontRuntimeProps & {
    fallbackSiteName: string;
    previewAdapter: ShopStorefrontPreviewAdapter;
};

function shopStorefrontRuntimeDocument(root: HTMLElement | null): Document | null {
    return root?.ownerDocument ?? null;
}

type ShopStorefrontRuntimeView = Window & typeof globalThis;

function shopStorefrontRuntimeView(root: HTMLElement | null): ShopStorefrontRuntimeView | null {
    const view = shopStorefrontRuntimeDocument(root)?.defaultView;

    return view ? view as ShopStorefrontRuntimeView : null;
}

function shopStorefrontRuntimeStorage(
    root: HTMLElement | null,
    storageName: "localStorage" | "sessionStorage",
): Storage | null {
    const view = shopStorefrontRuntimeView(root);

    if (view === null) {
        return null;
    }

    try {
        return view[storageName];
    } catch {
        return null;
    }
}

export type ShopPageContextOverride = {
    incomingIdentity: string;
    incomingVersion: number;
    pageContext: ShopPageContext;
};

export function shopPageContextIdentity(
    context: ShopPageContext | null | undefined,
): string {
    if (!context) {
        return "none";
    }

    return JSON.stringify([
        context.languageCode.trim().toLowerCase(),
        context.routeType.trim(),
        context.pageType.trim(),
        pageContextPath(context),
    ]);
}

export function resolvedShopPageContext(
    incoming: ShopPageContext | null | undefined,
    override: ShopPageContextOverride | null,
    incomingVersion = 0,
): ShopPageContext | null | undefined {
    return override?.incomingIdentity === shopPageContextIdentity(incoming)
        && override.incomingVersion === incomingVersion
        ? override.pageContext
        : incoming;
}

export function shopStorefrontNavigationEnabled(editorMode: boolean): boolean {
    return !editorMode;
}

const ShopStorefrontLinkContext = createContext<TemplateLinkComponent>(DefaultTemplateLink);

export function ShopStorefrontLink({
    href,
    children,
    ...props
}: Omit<TemplateLinkProps, "href"> & { href?: string | null }) {
    const LinkComponent = useContext(ShopStorefrontLinkContext);

    if (!href) {
        return <a {...props}>{children}</a>;
    }

    return <LinkComponent {...props} href={href}>{children}</LinkComponent>;
}

export function useShopStorefrontRuntime({
    siteName,
    pages,
    page,
    pageContext,
    shopBasicData,
    cartApiBasePath,
    customerApiBasePath,
    previewCustomerSession,
    onStorefrontLanguageSelect,
    onStorefrontNavigate,
    storefrontLocationHref,
    shopResolveApiPath,
    shopSearchApiPath,
    manufacturingProfileApiBasePath,
    packetaApiKey,
    LinkComponent = DefaultTemplateLink,
    design,
    editorMode,
    fallbackSiteName,
    previewAdapter,
}: ShopStorefrontRuntimeHookProps) {
    const incomingPageContextIdentity = shopPageContextIdentity(pageContext);
    const previousIncomingIdentityRef = useRef(incomingPageContextIdentity);
    const incomingPageContextVersionRef = useRef(0);

    if (previousIncomingIdentityRef.current !== incomingPageContextIdentity) {
        previousIncomingIdentityRef.current = incomingPageContextIdentity;
        incomingPageContextVersionRef.current += 1;
    }

    const incomingPageContextVersion = incomingPageContextVersionRef.current;
    const incomingPageContextSnapshotRef = useRef({
        identity: incomingPageContextIdentity,
        version: incomingPageContextVersion,
    });
    incomingPageContextSnapshotRef.current = {
        identity: incomingPageContextIdentity,
        version: incomingPageContextVersion,
    };
    const [pageContextOverride, setPageContextOverride] = useState<ShopPageContextOverride | null>(null);
    const livePageContext = resolvedShopPageContext(pageContext, pageContextOverride, incomingPageContextVersion);
    const setLivePageContext = useCallback((nextPageContext: ShopPageContext) => {
        if (
            incomingPageContextSnapshotRef.current.identity !== incomingPageContextIdentity
            || incomingPageContextSnapshotRef.current.version !== incomingPageContextVersion
        ) {
            return;
        }

        setPageContextOverride({
            incomingIdentity: incomingPageContextIdentity,
            incomingVersion: incomingPageContextVersion,
            pageContext: nextPageContext,
        });
    }, [incomingPageContextIdentity, incomingPageContextVersion]);
    const configuredHeaderBlock = useMemo(() => {
        const liveBlocks = livePageContext?.contentJson?.blocks;
        const globalBlocks = page?.globalLayout?.header?.blocks;

        for (const block of Array.isArray(liveBlocks) ? liveBlocks : []) {
            const header = findShopHeaderRoot(block);

            if (header) {
                return header;
            }
        }

        for (const block of Array.isArray(globalBlocks) ? globalBlocks : []) {
            const header = findShopHeaderRoot(block);

            if (header) {
                return header;
            }
        }

        return null;
    }, [livePageContext?.contentJson?.blocks, page?.globalLayout?.header?.blocks]);
    const headerMenuItems = useMemo(() => {
        const requestedLanguageCode = livePageContext?.languageCode ?? pageContext?.languageCode ?? page?.languageCode;
        const baseItems = resolveShopRuntimeNavigationItems({
            basicData: shopBasicData,
            header: configuredHeaderBlock,
            languageCode: requestedLanguageCode,
            pages,
            viewport: "desktop",
        });

        return enrichStoreMenuItems(baseItems, pageContext);
    }, [configuredHeaderBlock, livePageContext?.languageCode, page?.languageCode, pageContext, pages, shopBasicData]);
    const mobileMenuItems = useMemo(() => {
        const requestedLanguageCode = livePageContext?.languageCode ?? pageContext?.languageCode ?? page?.languageCode;
        const baseItems = resolveShopRuntimeNavigationItems({
            basicData: shopBasicData,
            header: configuredHeaderBlock,
            languageCode: requestedLanguageCode,
            pages,
            viewport: "mobile",
        });

        return enrichStoreMenuItems(baseItems, pageContext);
    }, [configuredHeaderBlock, livePageContext?.languageCode, page?.languageCode, pageContext, pages, shopBasicData]);
    const [searchOpen, setSearchOpen] = useState(false);
    const [cartOpen, setCartOpen] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [mobileTab, setMobileTab] = useState<MobileTab>(mobileMenuItems[0]?.uuid ?? "");
    const [previewSectionUuid, setPreviewSectionUuid] = useState<string | null>(null);
    const [toastVisible, setToastVisible] = useState(false);
    const [favoriteProductIds, setFavoriteProductIds] = useState<Set<string>>(() => new Set());
    const [favoriteProductPreviewMap, setFavoriteProductPreviewMap] = useState<Record<string, FavoriteProductPreview>>({});
    const [recentlyViewedProducts, setRecentlyViewedProducts] = useState<FavoriteProductPreview[]>([]);
    const [cart, setCart] = useState<PublicCart>(emptyCart);
    const [cartLoading, setCartLoading] = useState(false);
    const [cartError, setCartError] = useState<string | null>(null);
    const [addingProductUuid, setAddingProductUuid] = useState<string | null>(null);
    const [checkoutOptions, setCheckoutOptions] = useState<PublicCheckoutOptions>(emptyCheckoutOptions);
    const [checkoutOptionsLoading, setCheckoutOptionsLoading] = useState(false);
    const [checkoutOptionsError, setCheckoutOptionsError] = useState<string | null>(null);
    const [checkoutForm, setCheckoutForm] = useState<CheckoutFormState>(emptyCheckoutForm);
    const [checkoutSubmitting, setCheckoutSubmitting] = useState(false);
    const [checkoutError, setCheckoutError] = useState<string | null>(null);
    const [createdOrder, setCreatedOrder] = useState<PublicOrderCreated | null>(null);
    const [customerSession, setCustomerSession] = useState<PublicCustomerSession>(() => previewCustomerSession ?? {
        authenticated: false,
        customer: null,
    });
    const [customerAddresses, setCustomerAddresses] = useState<PublicCustomerAddress[]>([]);
    const [customerLoading, setCustomerLoading] = useState(false);
    const [customerError, setCustomerError] = useState<string | null>(null);
    const [catalogUpdating, setCatalogUpdating] = useState(false);
    const [browserLocation, setBrowserLocation] = useState<{ pathname: string; search: string; hash: string } | null>(null);
    const [selectedLanguageCode, setSelectedLanguageCode] = useState<string | null>(null);
    const [selectedCurrencyCode, setSelectedCurrencyCode] = useState<string | null>(null);
    const [newsletterStartedAt] = useState(templateLeadStartedAt);
    const catalogAbortControllerRef = useRef<AbortController | null>(null);
    const runtimeRootRef = useRef<HTMLSpanElement | null>(null);

    const customerUuid = customerSession.authenticated ? customerSession.customer?.uuid ?? null : null;
    const supportedLanguages = shopBasicData?.languages ?? EMPTY_SHOP_LANGUAGES;
    const supportedCurrencies = shopBasicData?.currencies ?? EMPTY_SHOP_CURRENCIES;
    const supportedLanguageKey = useMemo(
        () => supportedLanguages.map((language) => `${language.code}:${language.isDefault ? "1" : "0"}`).join("|"),
        [supportedLanguages],
    );
    const supportedCurrencyKey = useMemo(
        () => supportedCurrencies.map((currency) => `${currency.code}:${currency.isDefault ? "1" : "0"}`).join("|"),
        [supportedCurrencies],
    );
    const defaultLanguage = supportedLanguages.find((language) => language.isDefault)
        ?? supportedLanguages[0]
        ?? null;
    const contentLanguage = supportedLanguages.find((language) => language.code.toLowerCase() === livePageContext?.languageCode?.toLowerCase())
        ?? defaultLanguage
        ?? null;
    const storefrontLanguageCode = contentLanguage?.code
        ?? livePageContext?.languageCode
        ?? page?.languageCode
        ?? "en";
    const storefrontMessage = useCallback(
        (key: ShopStorefrontMessageKey, values: ShopStorefrontMessageValues = {}) => (
            shopStorefrontMessage(storefrontLanguageCode, key, values)
        ),
        [storefrontLanguageCode],
    );
    const storefrontHref = useCallback(
        (href: string) => localizedStorefrontHref(href, storefrontLanguageCode),
        [storefrontLanguageCode],
    );
    const currentLanguage = supportedLanguages.find((language) => language.code.toLowerCase() === selectedLanguageCode?.toLowerCase())
        ?? contentLanguage
        ?? supportedLanguages.find((language) => language.isDefault)
        ?? null;
    const defaultCurrency = supportedCurrencies.find((currency) => currency.isDefault)
        ?? supportedCurrencies[0]
        ?? null;
    const currentCurrency = supportedCurrencies.find((currency) => currency.code.toUpperCase() === selectedCurrencyCode?.toUpperCase())
        ?? defaultCurrency
        ?? null;
    const storefrontFormatting = useMemo<ShopStorefrontFormatting>(() => {
        const locale = storefrontLocale(storefrontLanguageCode);
        const currencyCode = currentCurrency?.code ?? "EUR";

        return {
            formatDate: (value) => formatDate(value, storefrontLanguageCode),
            formatMoney: (value, valueCurrencyCode) => formatMoney(
                value,
                storefrontLanguageCode,
                valueCurrencyCode ?? currencyCode,
            ),
            formatNumber: (value) => new Intl.NumberFormat(locale).format(value),
            locale,
        };
    }, [currentCurrency?.code, storefrontLanguageCode]);
    const displayName = siteName.trim() !== "" ? siteName.trim() : fallbackSiteName;
    const cartFlow: "multiStep" | "singlePage" = design.components.cartFlow === "singlePage" ? "singlePage" : "multiStep";
    const homeHref = useMemo(
        () => localizedStorefrontHref("/", storefrontLanguageCode),
        [storefrontLanguageCode],
    );
    const checkoutBaseHref = useMemo(
        () => pageHref(pages, "checkout", "/objednavka", storefrontLanguageCode),
        [pages, storefrontLanguageCode],
    );
    const cartPageHref = useMemo(
        () => pageHref(pages, "cart", "/kosik", storefrontLanguageCode),
        [pages, storefrontLanguageCode],
    );
    const checkoutHrefs = useMemo(
        () => checkoutFlowHrefs(checkoutBaseHref, cartPageHref, cartFlow),
        [cartFlow, cartPageHref, checkoutBaseHref],
    );
    const cartHref = checkoutHrefs.cart;
    const checkoutHref = checkoutHrefs.checkout;
    const checkoutContactHref = checkoutHrefs.contact;
    const checkoutDoneHref = checkoutHrefs.completed;
    const completedOrderStorageKey = useMemo(
        () => checkoutCompletedOrderStorageKey(checkoutDoneHref),
        [checkoutDoneHref],
    );
    const searchHref = useMemo(
        () => pageHref(pages, "search", "/vyhledavani", storefrontLanguageCode),
        [pages, storefrontLanguageCode],
    );
    const storesHref = useMemo(
        () => pageHref(
            pages,
            "storeList",
            storefrontSpecialRouteHref("stores", storefrontLanguageCode),
            storefrontLanguageCode,
        ),
        [pages, storefrontLanguageCode],
    );
    const accountHref = useMemo(
        () => pageHref(pages, "customerAccount", "/ucet", storefrontLanguageCode),
        [pages, storefrontLanguageCode],
    );
    const orderApiBasePath = useMemo(() => publicOrderApiBasePath(cartApiBasePath), [cartApiBasePath]);
    const currentPagePath = useMemo(() => pageContextPath(livePageContext), [livePageContext]);
    const currentBrowserPath = browserLocation?.pathname ?? currentPagePath;
    const currentBrowserSearch = browserLocation?.search ?? "";
    const currentBrowserHash = browserLocation?.hash ?? "";
    const languageOptions = useMemo<HeaderSelectorOption[]>(
        () => supportedLanguages.map((language) => ({
            code: language.code,
            label: language.name,
            href: hrefForLanguage(
                currentBrowserPath,
                currentBrowserSearch,
                currentBrowserHash,
                language.code,
                livePageContext?.languageRoutes,
                livePageContext?.canonicalHref,
            ),
            active: language.code.toLowerCase() === currentLanguage?.code.toLowerCase(),
        })),
        [
            currentBrowserHash,
            currentBrowserPath,
            currentBrowserSearch,
            currentLanguage?.code,
            livePageContext?.canonicalHref,
            livePageContext?.languageRoutes,
            supportedLanguages,
        ],
    );
    const currencyOptions = useMemo<HeaderSelectorOption[]>(
        () => supportedCurrencies.map((currency) => ({
            code: currency.code,
            label: currency.code,
            href: hrefForCurrency(currentBrowserPath, currentBrowserSearch, currency.code),
            active: currency.code.toUpperCase() === currentCurrency?.code.toUpperCase(),
        })),
        [currentBrowserPath, currentBrowserSearch, currentCurrency?.code, supportedCurrencies],
    );
    const activeMenuUuids = useMemo(() => activeMenuUuidsFromContext(livePageContext), [livePageContext]);
    const activeSection = useMemo(
        () => activeMenuSection(headerMenuItems, currentPagePath, activeMenuUuids, previewSectionUuid),
        [activeMenuUuids, currentPagePath, headerMenuItems, previewSectionUuid],
    );
    const activeSectionItems = useMemo(() => sectionMenuItems(activeSection), [activeSection]);
    const storeCities = useMemo(() => storeCitiesFromContext(livePageContext), [livePageContext]);
    const contextFavoritePreviews = useMemo(
        () => previewAdapter.productsFromContext(livePageContext, storefrontFormatting.formatMoney, storefrontHref),
        [livePageContext, previewAdapter, storefrontFormatting.formatMoney, storefrontHref],
    );
    const currentProductPreview = useMemo(
        () => previewAdapter.currentProductFromContext(livePageContext, storefrontFormatting.formatMoney, storefrontHref),
        [livePageContext, previewAdapter, storefrontFormatting.formatMoney, storefrontHref],
    );
    const favoriteProducts = useMemo(
        () => previewAdapter.productsForIds(favoriteProductIds, favoriteProductPreviewMap, storefrontMessage, storefrontFormatting.formatMoney),
        [favoriteProductIds, favoriteProductPreviewMap, previewAdapter, storefrontFormatting.formatMoney, storefrontMessage],
    );
    const recommendedProductPreviews = useMemo(() => {
        const previews = [
            ...favoriteProducts,
            ...recentlyViewedProducts,
            ...contextFavoritePreviews,
        ];
        const seen = new Set<string>();

        return previews.filter((product) => {
            if (product.id === currentProductPreview?.id || seen.has(product.id)) {
                return false;
            }

            seen.add(product.id);

            return true;
        });
    }, [contextFavoritePreviews, currentProductPreview?.id, favoriteProducts, recentlyViewedProducts]);
    const visibleFavoriteProductIds = useMemo(
        () => customerUuid !== null ? favoriteProductIds : new Set<string>(),
        [customerUuid, favoriteProductIds],
    );
    const visibleFavoriteProducts = useMemo(
        () => customerUuid !== null ? favoriteProducts : [],
        [customerUuid, favoriteProducts],
    );
    const rawPageContentBlocks = livePageContext?.contentJson?.blocks;
    const pageContentBlocks = useMemo(() => (
        Array.isArray(rawPageContentBlocks)
            ? rawPageContentBlocks.map((block) => {
                const localizedBlock = withLocalizedStorefrontDefaults(block, storefrontLanguageCode);

                return editorMode ? localizedBlock : withoutProductionDemoContent(localizedBlock);
            })
            : rawPageContentBlocks
    ), [editorMode, rawPageContentBlocks, storefrontLanguageCode]);
    const localizedPageContext = useMemo<ShopPageContext | null | undefined>(() => {
        if (!livePageContext?.contentJson || !Array.isArray(pageContentBlocks)) {
            return livePageContext;
        }

        return {
            ...livePageContext,
            contentJson: {
                ...livePageContext.contentJson,
                blocks: pageContentBlocks,
            },
        };
    }, [livePageContext, pageContentBlocks]);
    const animationRefreshKey = useMemo(
        () => JSON.stringify(animationRefreshSnapshot(Array.isArray(pageContentBlocks) ? pageContentBlocks : [])),
        [pageContentBlocks],
    );
    useReplayAppearanceAnimations(animationRefreshKey, editorMode);
    const pageContentVersion = Number(livePageContext?.contentJson?.shopBuilderSchemaVersion);
    const normalizedPageContentVersion = Number.isFinite(pageContentVersion) ? pageContentVersion : 0;
    const promoBarBlockIndex = Array.isArray(pageContentBlocks) ? pageContentBlocks.findIndex((block) => (
        block.type === "promoBar"
        || isLagunaRuntimeRegion(block, "promoBar")
    )) : -1;
    const rawPromoBarBlock = promoBarBlockIndex >= 0 && Array.isArray(pageContentBlocks) ? pageContentBlocks[promoBarBlockIndex] ?? null : null;
    const promoBarBlock = rawPromoBarBlock ? lagunaRuntimeBlock(rawPromoBarBlock) : null;
    const headerBlockIndex = Array.isArray(pageContentBlocks) ? pageContentBlocks.findIndex((block) => (
        block.type === "header"
        || isLagunaRuntimeRegion(block, "header")
    )) : -1;
    const rawHeaderBlock = headerBlockIndex >= 0 && Array.isArray(pageContentBlocks) ? pageContentBlocks[headerBlockIndex] ?? null : null;
    const headerBlock = rawHeaderBlock ? lagunaRuntimeBlock(rawHeaderBlock) : null;
    const hasFooterBlock = Array.isArray(pageContentBlocks) && pageContentBlocks.some((block) => (
        block.type === "footer"
        || (block.type === "richContent" && block.options?.morrowRegion === "footer")
        || isLagunaRuntimeRegion(block, "footer")
    ));
    const showLegacyPromoBarFallback = design.components.header !== "minimal"
        && promoBarBlock === null
        && (!Number.isFinite(pageContentVersion) || pageContentVersion < SHOP_BUILDER_SCHEMA_VERSION);
    const showLegacyHeaderFallback = headerBlock === null
        && (!Number.isFinite(pageContentVersion) || pageContentVersion < SHOP_BUILDER_SCHEMA_VERSION);
    const showLegacyFooterFallback = !hasFooterBlock && (!Number.isFinite(pageContentVersion) || pageContentVersion < SHOP_BUILDER_SCHEMA_VERSION);
    const shopSiteInfo = useMemo(() => ({
        footerVariant: design.components.footer,
        homeHref,
        siteName: displayName,
        storesHref,
    }), [design.components.footer, displayName, homeHref, storesHref]);
    const [checkoutRouteStep, setCheckoutRouteStep] = useState<CheckoutRouteStep>("deliveryPayment");

    useEffect(() => {
        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (editorMode && !storefrontLocationHref) {
            return undefined;
        }

        if (view === null && !storefrontLocationHref) {
            return undefined;
        }

        const supportedLanguageCodes = new Set(supportedLanguages.map((language) => language.code.toLowerCase()));
        const supportedCurrencyCodes = new Set(supportedCurrencies.map((currency) => currency.code.toUpperCase()));
        const defaultLanguageCode = contentLanguage?.code ?? defaultLanguage?.code ?? null;
        const defaultCode = defaultCurrency?.code ?? null;
        const syncLocationState = () => {
            const location = storefrontLocationHref
                ? new URL(storefrontLocationHref, "https://shop.preview")
                : view?.location;

            if (!location) {
                return;
            }

            const search = location.search;
            const pathLanguage = storefrontLanguagePrefix(
                location.pathname,
                supportedLanguageCodes,
            )?.languageCode ?? null;
            const requestedLanguage = new URLSearchParams(search).get(LANGUAGE_QUERY_PARAM)?.toLowerCase() ?? null;
            const requestedCurrency = new URLSearchParams(search).get(CURRENCY_QUERY_PARAM)?.toUpperCase() ?? null;
            const nextLanguage = pathLanguage
                ?? (requestedLanguage !== null && supportedLanguageCodes.has(requestedLanguage)
                    ? requestedLanguage
                    : defaultLanguageCode);
            const nextCurrency = requestedCurrency !== null && supportedCurrencyCodes.has(requestedCurrency)
                ? requestedCurrency
                : defaultCode;

            setBrowserLocation((current) => {
                const nextLocation = {
                    pathname: location.pathname,
                    search,
                    hash: location.hash,
                };

                return current?.pathname === nextLocation.pathname
                    && current.search === nextLocation.search
                    && current.hash === nextLocation.hash
                    ? current
                    : nextLocation;
            });
            setSelectedLanguageCode((current) => (current === nextLanguage ? current : nextLanguage));
            setSelectedCurrencyCode((current) => (current === nextCurrency ? current : nextCurrency));
        };

        syncLocationState();

        if (storefrontLocationHref || view === null) {
            return undefined;
        }

        view.addEventListener("popstate", syncLocationState);

        return () => view.removeEventListener("popstate", syncLocationState);
    }, [contentLanguage?.code, defaultCurrency?.code, defaultLanguage?.code, editorMode, storefrontLocationHref, supportedCurrencyKey, supportedLanguageKey]);

    useEffect(() => {
        const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");

        setFavoriteProductPreviewMap(readFavoriteProductPreviewMap(storage));
        setRecentlyViewedProducts(readRecentlyViewedProducts(storage));
    }, []);

    useEffect(() => {
        const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");

        setFavoriteProductIds(readFavoriteProductIds(customerUuid, storage));
    }, [customerUuid]);

    useEffect(() => {
        if (contextFavoritePreviews.length === 0) {
            return;
        }

        const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");

        setFavoriteProductPreviewMap((current) => {
            const next = { ...current };
            let changed = false;

            for (const preview of contextFavoritePreviews) {
                if (JSON.stringify(next[preview.id]) !== JSON.stringify(preview)) {
                    next[preview.id] = preview;
                    changed = true;
                }
            }

            if (changed) {
                writeFavoriteProductPreviewMap(next, storage);
            }

            return changed ? next : current;
        });
    }, [contextFavoritePreviews]);

    useEffect(() => {
        if (currentProductPreview === null) {
            return;
        }

        const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");

        setRecentlyViewedProducts(addRecentlyViewedProduct(currentProductPreview, storage));
    }, [currentProductPreview]);

    useEffect(() => {
        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (view === null) {
            return undefined;
        }

        const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");
        const favoriteStorageKey = customerUuid !== null ? favoriteProductIdsStorageKey(customerUuid) : null;

        const handleStorage = (event: StorageEvent) => {
            if (favoriteStorageKey !== null && event.key === favoriteStorageKey) {
                setFavoriteProductIds(readFavoriteProductIds(customerUuid, storage));
            }

            if (event.key === FAVORITE_PRODUCTS_STORAGE_KEY) {
                setFavoriteProductPreviewMap(readFavoriteProductPreviewMap(storage));
            }
        };

        view.addEventListener("storage", handleStorage);

        return () => view.removeEventListener("storage", handleStorage);
    }, [customerUuid]);

    useEffect(() => {
        if (mobileMenuItems.length === 0) {
            if (mobileTab !== "") {
                setMobileTab("");
            }

            return;
        }

        if (!mobileMenuItems.some((item) => item.uuid === mobileTab)) {
            setMobileTab(mobileMenuItems[0]?.uuid ?? "");
        }
    }, [mobileMenuItems, mobileTab]);

    useEffect(() => {
        if (!toastVisible) return undefined;

        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (view === null) {
            return undefined;
        }

        const timeout = view.setTimeout(() => setToastVisible(false), 3000);

        return () => view.clearTimeout(timeout);
    }, [toastVisible]);

    useEffect(() => {
        if (editorMode && !storefrontLocationHref) {
            return undefined;
        }

        const view = shopStorefrontRuntimeView(runtimeRootRef.current);
        const syncCheckoutStep = () => {
            const pathname = storefrontLocationHref
                ? new URL(storefrontLocationHref, "https://shop.preview").pathname
                : view?.location.pathname;

            if (pathname) {
                setCheckoutRouteStep(checkoutRouteStepFromPath(checkoutBaseHref, pathname));
            }
        };

        syncCheckoutStep();

        if (storefrontLocationHref || view === null) {
            return undefined;
        }

        view.addEventListener("popstate", syncCheckoutStep);

        return () => view.removeEventListener("popstate", syncCheckoutStep);
    }, [checkoutBaseHref, editorMode, storefrontLocationHref]);

    useEffect(() => {
        if (checkoutRouteStep !== "completed" || createdOrder !== null) {
            return;
        }

        let storedOrder: PublicOrderCreated | null = null;

        try {
            const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "sessionStorage");
            storedOrder = parseStoredPublicOrderCreated(storage?.getItem(completedOrderStorageKey) ?? null);
        } catch {
            // The route still renders a generic completion when storage is unavailable.
        }

        if (storedOrder !== null) {
            setCreatedOrder(storedOrder);
        }
    }, [checkoutRouteStep, completedOrderStorageKey, createdOrder]);

    useEffect(() => {
        if (previewCustomerSession !== undefined) {
            setCustomerSession(previewCustomerSession);
            setCustomerLoading(false);
            setCustomerError(null);

            return undefined;
        }

        if (!customerApiBasePath) {
            setCustomerSession({ authenticated: false, customer: null });

            return undefined;
        }

        let cancelled = false;

        async function loadCustomerSession() {
            setCustomerLoading(true);

            try {
                const response = await fetch(`${customerApiBasePath}/me`, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    return;
                }

                const nextSession = await response.json() as PublicCustomerSession;

                if (!cancelled) {
                    setCustomerSession(nextSession);
                    setCustomerError(null);
                }
            } catch {
                if (!cancelled) {
                    setCustomerSession({ authenticated: false, customer: null });
                }
            } finally {
                if (!cancelled) {
                    setCustomerLoading(false);
                }
            }
        }

        void loadCustomerSession();

        return () => {
            cancelled = true;
        };
    }, [customerApiBasePath, previewCustomerSession]);

    useEffect(() => {
        const customer = customerSession.customer;

        if (!customerSession.authenticated || customer === null) {
            return;
        }

        setCheckoutForm((current) => checkoutFormWithCustomerDefaults(current, customer, customerAddresses));
    }, [
        customerAddresses,
        customerSession.authenticated,
        customerSession.customer?.uuid,
        customerSession.customer?.email,
        customerSession.customer?.firstname,
        customerSession.customer?.lastname,
        customerSession.customer?.company,
        customerSession.customer?.ic,
        customerSession.customer?.vat,
    ]);

    useEffect(() => {
        if (!customerSession.authenticated || customerSession.customer === null || !customerApiBasePath) {
            setCustomerAddresses([]);

            return undefined;
        }

        let cancelled = false;
        const apiPath = customerApiBasePath;

        async function loadCustomerAddresses() {
            try {
                const response = await fetch(`${apiPath}/addresses`, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    return;
                }

                const payload = await response.json() as PublicCustomerAddressListResponse;

                if (!cancelled) {
                    setCustomerAddresses(payload.addresses ?? []);
                }
            } catch {
                if (!cancelled) {
                    setCustomerAddresses([]);
                }
            }
        }

        void loadCustomerAddresses();

        return () => {
            cancelled = true;
        };
    }, [customerApiBasePath, customerSession.authenticated, customerSession.customer?.uuid]);

    function navigateCheckoutRouteStep(step: CheckoutRouteStep) {
        if (!shopStorefrontNavigationEnabled(editorMode)) {
            return;
        }

        setCheckoutRouteStep(step);

        const nextHref = step === "cart"
            ? cartHref
            : step === "contact"
                ? checkoutContactHref
                : step === "completed"
                    ? checkoutDoneHref
                    : checkoutHref;

        if (onStorefrontNavigate) {
            onStorefrontNavigate(nextHref);

            return;
        }

        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (view === null) {
            return;
        }

        if (view.location.pathname !== nextHref) {
            view.history.pushState({}, "", nextHref);
            const nextUrl = new URL(nextHref, view.location.origin);
            setBrowserLocation({
                pathname: nextUrl.pathname,
                search: nextUrl.search,
                hash: nextUrl.hash,
            });
        }
    }

    const navigateCatalog = useCallback(async (href: string, updateHistory = true): Promise<void> => {
        if (!shopStorefrontNavigationEnabled(editorMode)) {
            return;
        }

        if (onStorefrontNavigate) {
            onStorefrontNavigate(href);

            return;
        }

        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (view === null) {
            return;
        }

        if (!shopResolveApiPath) {
            view.location.assign(href);

            return;
        }

        const target = new URL(href, view.location.origin);

        if (storefrontSpecialRouteMatch(target.pathname)?.route === "sale" && !target.searchParams.has(CATALOG_SALE_RETURN_PARAM)) {
            const currentUrl = new URL(view.location.href);
            const returnTo = safeCatalogReturnHref(currentUrl.searchParams.get(CATALOG_SALE_RETURN_PARAM));

            if (returnTo !== null) {
                target.searchParams.set(CATALOG_SALE_RETURN_PARAM, returnTo);
            }
        }

        if (currentCurrency?.code && !target.searchParams.has(CURRENCY_QUERY_PARAM)) {
            target.searchParams.set(CURRENCY_QUERY_PARAM, currentCurrency.code);
        }

        const hasLanguagePrefix = storefrontLanguagePrefix(
            target.pathname,
            supportedLanguages.map((language) => language.code),
        ) !== null;

        if (hasLanguagePrefix) {
            target.searchParams.delete(LANGUAGE_QUERY_PARAM);
        } else if (selectedLanguageCode && !target.searchParams.has(LANGUAGE_QUERY_PARAM)) {
            target.searchParams.set(LANGUAGE_QUERY_PARAM, selectedLanguageCode);
        }

        const nextHref = target.pathname + target.search;
        const currentHref = view.location.pathname + view.location.search;

        if (nextHref === currentHref && updateHistory) {
            return;
        }

        catalogAbortControllerRef.current?.abort();
        const abortController = new AbortController();
        catalogAbortControllerRef.current = abortController;
        const search = new URLSearchParams(target.search);
        search.set("path", target.pathname);
        setCatalogUpdating(true);

        try {
            const response = await fetch(`${shopResolveApiPath}?${search.toString()}`, {
                credentials: "same-origin",
                cache: "no-store",
                signal: abortController.signal,
            });

            if (!response.ok) {
                view.location.assign(href);

                return;
            }

            const payload = await response.json() as ShopResolvePayload;

            if (abortController.signal.aborted) {
                return;
            }

            if (!payload.pageContext) {
                view.location.assign(href);

                return;
            }

            const ownerDocument = shopStorefrontRuntimeDocument(runtimeRootRef.current);
            const activeElement = ownerDocument?.activeElement;

            if (
                activeElement instanceof view.HTMLInputElement
                && activeElement.dataset.catalogSearchInput === "true"
                && activeElement.dataset.catalogTargetHref
                && activeElement.dataset.catalogTargetHref !== nextHref
            ) {
                return;
            }

            setLivePageContext(payload.pageContext);
            setBrowserLocation((current) => {
                const nextLocation = {
                    pathname: target.pathname,
                    search: target.search,
                    hash: target.hash,
                };

                return current?.pathname === nextLocation.pathname
                    && current.search === nextLocation.search
                    && current.hash === nextLocation.hash
                    ? current
                    : nextLocation;
            });
            setSearchOpen(false);

            if (updateHistory && nextHref !== currentHref) {
                view.history.pushState({}, "", nextHref);
            }
        } catch {
            if (abortController.signal.aborted) {
                return;
            }

            view.location.assign(href);
        } finally {
            if (catalogAbortControllerRef.current === abortController) {
                catalogAbortControllerRef.current = null;
                setCatalogUpdating(false);
            }
        }
    }, [currentCurrency?.code, editorMode, onStorefrontNavigate, selectedLanguageCode, setLivePageContext, shopResolveApiPath, supportedLanguages]);

    useEffect(() => {
        if (editorMode || onStorefrontNavigate || storefrontLocationHref || !shopResolveApiPath) {
            return undefined;
        }

        const view = shopStorefrontRuntimeView(runtimeRootRef.current);

        if (view === null) {
            return undefined;
        }

        const syncCatalogFromHistory = () => {
            void navigateCatalog(view.location.pathname + view.location.search, false);
        };

        view.addEventListener("popstate", syncCatalogFromHistory);

        return () => view.removeEventListener("popstate", syncCatalogFromHistory);
    }, [editorMode, navigateCatalog, onStorefrontNavigate, shopResolveApiPath, storefrontLocationHref]);

    useEffect(() => {
        if (!cartApiBasePath) {
            setCart(emptyCart);

            return undefined;
        }

        let cancelled = false;
        const cartApiUrl = pathWithCurrency(cartApiBasePath, currentCurrency?.code);

        async function loadCart() {
            setCartLoading(true);

            try {
                const response = await fetch(cartApiUrl, {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    return;
                }

                const nextCart = await response.json() as PublicCart;

                if (!cancelled) {
                    setCart(nextCart);
                    setCartError(null);
                }
            } catch {
                if (!cancelled) {
                    setCart(emptyCart);
                    setCartError(storefrontMessage("ui.error.cartLoad"));
                }
            } finally {
                if (!cancelled) {
                    setCartLoading(false);
                }
            }
        }

        void loadCart();

        return () => {
            cancelled = true;
        };
    }, [cartApiBasePath, currentCurrency?.code]);

    useEffect(() => {
        const pageType = livePageContext?.pageType;

        if (!cartApiBasePath || (pageType !== "checkout" && pageType !== "cart")) {
            setCheckoutOptions(emptyCheckoutOptions);
            setCheckoutOptionsError(null);

            return undefined;
        }

        let cancelled = false;

        async function loadCheckoutOptions() {
            setCheckoutOptionsLoading(true);
            setCheckoutOptionsError(null);

            try {
                const response = await fetch(pathWithCurrency(
                    `${cartApiBasePath}/checkout-options`,
                    currentCurrency?.code,
                    checkoutForm.deliveryCountryCode,
                ), {
                    credentials: "same-origin",
                    cache: "no-store",
                });

                if (!response.ok) {
                    if (!cancelled) {
                        setCheckoutOptions(emptyCheckoutOptions);
                        setCheckoutOptionsError(storefrontMessage("ui.error.checkoutOptionsLoad"));
                    }

                    return;
                }

                const nextOptions = await response.json() as PublicCheckoutOptions;

                if (!cancelled) {
                    setCheckoutOptions(nextOptions);
                    setCheckoutOptionsError(null);
                }
            } catch {
                if (!cancelled) {
                    setCheckoutOptions(emptyCheckoutOptions);
                    setCheckoutOptionsError(storefrontMessage("ui.error.checkoutOptionsLoad"));
                }
            } finally {
                if (!cancelled) {
                    setCheckoutOptionsLoading(false);
                }
            }
        }

        void loadCheckoutOptions();

        return () => {
            cancelled = true;
        };
    }, [cart.itemsTotalPrice, cartApiBasePath, checkoutForm.deliveryCountryCode, currentCurrency?.code, livePageContext?.pageType]);

    function openSearch() {
        setCartOpen(false);
        setSearchOpen(true);
    }

    function openCart() {
        setSearchOpen(false);
        setCartOpen(true);
    }

    function closeOverlay() {
        setSearchOpen(false);
        setCartOpen(false);
    }

    function selectStorefrontLanguage(languageCode: string, href: string): true {
        if (!shopStorefrontNavigationEnabled(editorMode)) {
            return true;
        }

        setSelectedLanguageCode(languageCode);

        if (onStorefrontLanguageSelect) {
            onStorefrontLanguageSelect(languageCode, href);

            return true;
        }

        shopStorefrontRuntimeView(runtimeRootRef.current)?.location.assign(href);

        return true;
    }

    async function handleNewsletterSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
        event.preventDefault();

        if (editorMode || onStorefrontNavigate) {
            return;
        }

        const formElement = event.currentTarget;
        const formView = formElement.ownerDocument.defaultView;

        if (formView === null) {
            return;
        }

        const fields = siteBuilderLeadValuesFromFormData(new formView.FormData(formElement));
        try {
            const form = await resolveSiteBuilderLeadForm(
                "/api/lead/form",
                "newsletter",
            );
            const result = await submitSiteBuilderLead({
                endpoint: "/api/lead/submit",
                fields,
                form,
                honeypotValue: typeof fields.honeypotValue === "string"
                    ? fields.honeypotValue
                    : "",
                landingUrl: storefrontLocationHref
                    ?? formView.location.href,
                purpose: "newsletter",
                startedAt: newsletterStartedAt,
            });

            if (result.ok) {
                formElement.reset();
                setToastVisible(true);
            }
        } catch {
            // Keep the authored value so the visitor can retry.
        }
    }

    function toggleFavorite(productId: string) {
        if (editorMode) {
            return;
        }

        if (customerUuid === null) {
            void navigateCatalog(accountHref);

            return;
        }

        setFavoriteProductIds((current) => {
            const next = new Set(current);

            if (next.has(productId)) {
                next.delete(productId);
            } else {
                next.add(productId);
            }

            const storage = shopStorefrontRuntimeStorage(runtimeRootRef.current, "localStorage");
            writeFavoriteProductIds(customerUuid, next, storage);

            return next;
        });
    }

    function updateCheckoutForm(patch: Partial<CheckoutFormState>) {
        setCheckoutForm((current) => ({
            ...current,
            ...patch,
        }));
        setCheckoutError(null);
    }

    async function customerRequest(
        path: string,
        body: Record<string, unknown> | null,
        fallbackError: string,
        method: "POST" | "PATCH" = "POST",
    ): Promise<PublicCustomerSession | null> {
        if (!customerApiBasePath) {
            setCustomerError(storefrontMessage("ui.error.accountUnavailable"));

            return null;
        }

        setCustomerLoading(true);
        setCustomerError(null);

        try {
            const response = await fetch(`${customerApiBasePath}${path}`, {
                method,
                credentials: "same-origin",
                cache: "no-store",
                headers: body === null ? undefined : {
                    "content-type": "application/json",
                },
                body: body === null ? undefined : JSON.stringify(body),
            });

            if (!response.ok) {
                setCustomerError(await customerErrorTextFromResponse(response, fallbackError, storefrontMessage));

                return null;
            }

            const nextSession = await response.json() as PublicCustomerSession;
            setCustomerSession(nextSession);
            if (nextSession.customer !== null) {
                setCheckoutForm((current) => ({
                    ...current,
                    email: current.email.trim() === "" ? nextSession.customer?.email ?? current.email : current.email,
                }));
            }
            setCustomerError(null);

            return nextSession;
        } catch {
            setCustomerError(fallbackError);

            return null;
        } finally {
            setCustomerLoading(false);
        }
    }

    async function loginCustomer(email: string, password: string): Promise<boolean> {
        const session = await customerRequest("/login", {
            email: email.trim(),
            password,
        }, storefrontMessage("ui.error.loginFailed"));

        return session?.authenticated ?? false;
    }

    async function registerCustomer(
        email: string,
        password: string,
        passwordConfirmation: string,
        newsletterOptOut: boolean,
    ): Promise<boolean> {
        const session = await customerRequest("/register", {
            email: email.trim(),
            password,
            passwordConfirmation,
            newsletterOptOut,
        }, storefrontMessage("ui.error.registrationFailed"));

        return session !== null;
    }

    async function updateCustomerProfile(profile: PublicCustomerProfileUpdate): Promise<boolean> {
        const session = await customerRequest("/profile", profile, storefrontMessage("ui.error.profileSave"), "PATCH");

        return session?.authenticated ?? false;
    }

    async function changeCustomerPassword(
        currentPassword: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ): Promise<boolean> {
        const session = await customerRequest("/password", {
            currentPassword,
            newPassword,
            newPasswordConfirmation,
        }, storefrontMessage("ui.error.passwordChange"));

        return session?.authenticated ?? false;
    }

    async function requestCustomerPasswordReset(email: string): Promise<boolean> {
        if (!customerApiBasePath) {
            setCustomerError(storefrontMessage("ui.error.accountUnavailable"));

            return false;
        }

        setCustomerLoading(true);
        setCustomerError(null);

        try {
            const response = await fetch(`${customerApiBasePath}/password-reset`, {
                method: "POST",
                credentials: "same-origin",
                cache: "no-store",
                headers: {
                    "content-type": "application/json",
                },
                body: JSON.stringify({
                    email: email.trim(),
                    resetPath: accountHref,
                }),
            });

            if (!response.ok) {
                setCustomerError(await customerErrorTextFromResponse(response, storefrontMessage("ui.error.passwordResetRequest"), storefrontMessage));

                return false;
            }

            return true;
        } catch {
            setCustomerError(storefrontMessage("ui.error.passwordResetRequest"));

            return false;
        } finally {
            setCustomerLoading(false);
        }
    }

    async function confirmCustomerPasswordReset(
        token: string,
        newPassword: string,
        newPasswordConfirmation: string,
    ): Promise<boolean> {
        const session = await customerRequest("/password-reset/confirm", {
            token,
            newPassword,
            newPasswordConfirmation,
        }, storefrontMessage("ui.error.passwordResetConfirm"));

        return session?.authenticated ?? false;
    }

    async function reorderCustomerOrder(orderUuid: string): Promise<PublicOrderReorderResponse | null> {
        if (orderApiBasePath === null) {
            setCartError(storefrontMessage("ui.error.ordersUnavailable"));

            return null;
        }

        setCartLoading(true);
        setCartError(null);

        try {
            const response = await fetch(pathWithCurrency(`${orderApiBasePath}/${encodeURIComponent(orderUuid)}/reorder`, currentCurrency?.code), {
                method: "POST",
                credentials: "same-origin",
                cache: "no-store",
            });

            if (!response.ok) {
                setCartError(await errorTextFromResponse(response, storefrontMessage("ui.error.orderReorder"), storefrontMessage));

                return null;
            }

            const payload = await response.json() as PublicOrderReorderResponse;
            setCart(payload.cart);
            setSearchOpen(false);
            setCartOpen(true);

            return payload;
        } catch {
            setCartError(storefrontMessage("ui.error.orderReorder"));

            return null;
        } finally {
            setCartLoading(false);
        }
    }

    async function logoutCustomer(): Promise<void> {
        if (!customerApiBasePath) {
            return;
        }

        setCustomerLoading(true);
        setCustomerError(null);

        try {
            await fetch(`${customerApiBasePath}/logout`, {
                method: "POST",
                credentials: "same-origin",
                cache: "no-store",
            });
        } finally {
            setCustomerSession({ authenticated: false, customer: null });
            setCustomerLoading(false);
        }
    }

    function nullableText(value: string): string | null {
        const trimmed = value.trim();

        return trimmed === "" ? null : trimmed;
    }

    async function submitCheckout(form: CheckoutFormState): Promise<boolean> {
        if (orderApiBasePath === null) {
            setCheckoutError(storefrontMessage("ui.error.ordersUnavailable"));

            return false;
        }

        setCheckoutSubmitting(true);
        setCheckoutError(null);

        try {
            const response = await fetch(pathWithCurrency(
                orderApiBasePath,
                currentCurrency?.code,
                form.deliveryCountryCode,
            ), {
                method: "POST",
                credentials: "same-origin",
                cache: "no-store",
                headers: {
                    "content-type": "application/json",
                },
                body: JSON.stringify({
                    email: form.email.trim(),
                    firstname: form.firstname.trim(),
                    lastname: form.lastname.trim(),
                    phone: nullableText(form.phone),
                    deliveryCompany: null,
                    deliveryStreet: form.deliveryStreet.trim(),
                    deliveryCity: form.deliveryCity.trim(),
                    deliveryPostcode: form.deliveryPostcode.trim(),
                    deliveryCountryCode: form.deliveryCountryCode.trim().toUpperCase(),
                    billingSameAsDelivery: form.billingSameAsDelivery,
                    billingFirstname: nullableText(form.billingFirstname),
                    billingLastname: nullableText(form.billingLastname),
                    billingCompany: nullableText(form.billingCompany),
                    billingIc: nullableText(form.billingIc),
                    billingVat: nullableText(form.billingVat),
                    billingPhone: nullableText(form.billingPhone),
                    billingStreet: nullableText(form.billingStreet),
                    billingCity: nullableText(form.billingCity),
                    billingPostcode: nullableText(form.billingPostcode),
                    billingCountryCode: nullableText(form.billingCountryCode)?.toUpperCase() ?? null,
                    note: nullableText(form.note),
                    termsAccepted: form.termsAccepted,
                }),
            });

            if (!response.ok) {
                setCheckoutError(await errorTextFromResponse(response, storefrontMessage("ui.error.orderSubmit"), storefrontMessage));
                await refreshCartAfterError();

                return false;
            }

            const order = await response.json() as PublicOrderCreated;
            try {
                shopStorefrontRuntimeStorage(runtimeRootRef.current, "sessionStorage")
                    ?.setItem(completedOrderStorageKey, JSON.stringify(order));
            } catch {
                // The generic completed route remains available when storage is blocked.
            }
            setCreatedOrder(order);
            setCart(emptyCart);
            setCheckoutOptions(emptyCheckoutOptions);
            setCheckoutForm(customerSession.customer !== null
                ? checkoutFormWithCustomerDefaults(emptyCheckoutForm, customerSession.customer, customerAddresses)
                : emptyCheckoutForm);
            setCartError(null);
            navigateCheckoutRouteStep("completed");

            return true;
        } catch {
            setCheckoutError(storefrontMessage("ui.error.orderSubmit"));

            return false;
        } finally {
            setCheckoutSubmitting(false);
        }
    }

    async function refreshCartAfterError(): Promise<void> {
        if (!cartApiBasePath) {
            return;
        }

        try {
            const response = await fetch(pathWithCurrency(cartApiBasePath, currentCurrency?.code), {
                credentials: "same-origin",
                cache: "no-store",
            });

            if (!response.ok) {
                return;
            }

            setCart(await response.json() as PublicCart);
        } catch {
            // The original error message is more useful than a failed background refresh.
        }
    }

    async function cartRequest(path: string, init: RequestInit, fallbackError: string): Promise<boolean> {
        if (!cartApiBasePath) {
            setCartError(storefrontMessage("ui.error.cartUnavailable"));

            return false;
        }

        setCartLoading(true);

        try {
            const headers = new Headers(init.headers);

            if (init.body && !headers.has("content-type")) {
                headers.set("content-type", "application/json");
            }

            const response = await fetch(pathWithCurrency(`${cartApiBasePath}${path}`, currentCurrency?.code), {
                ...init,
                credentials: "same-origin",
                cache: "no-store",
                headers,
            });

            if (!response.ok) {
                setCartError(await errorTextFromResponse(response, fallbackError, storefrontMessage));
                await refreshCartAfterError();

                return false;
            }

            setCart(await response.json() as PublicCart);
            setCartError(null);

            return true;
        } catch {
            setCartError(fallbackError);

            return false;
        } finally {
            setCartLoading(false);
        }
    }

    function updateCartItem(
        itemUuid: string,
        quantity: number,
        manufacturingSpecification?: PublicManufacturingSpecification,
    ) {
        const currentSpecification = manufacturingSpecification
            ?? cart.items.find((item) => item.uuid === itemUuid)?.manufacturingSpecification;

        void cartRequest(`/items/${encodeURIComponent(itemUuid)}`, {
            method: "PUT",
            body: JSON.stringify(publicCartItemUpdateBody(quantity, currentSpecification)),
        }, storefrontMessage("ui.error.quantityUpdate"));
    }

    function removeCartItem(itemUuid: string) {
        void cartRequest(`/items/${encodeURIComponent(itemUuid)}`, {
            method: "DELETE",
        }, storefrontMessage("ui.error.cartItemRemove"));
    }

    async function addCartItem(
        productUuid: string,
        productAttributeUuid: string | null = null,
        quantity = 1,
        surchargeValueUuids: string[] = [],
        manufacturingSpecification?: PublicManufacturingSpecification,
    ) {
        if (editorMode) {
            return;
        }

        setAddingProductUuid(productUuid);

        try {
            const body = publicCartItemAddBody({
                productUuid,
                productAttributeUuid,
                quantity,
                surchargeValueUuids,
                manufacturingSpecification,
            });

            const added = await cartRequest("/items", {
                method: "POST",
                body: JSON.stringify(body),
            }, storefrontMessage("ui.error.cartItemAdd"));

            if (added) {
                setSearchOpen(false);
                setCartOpen(true);
            }
        } finally {
            setAddingProductUuid(null);
        }
    }

    async function selectDeliveryPayment(
        carrierRateUuid: string,
        carrierPaymentOptionUuid: string,
        pickupPoint?: PickupPointSelection,
    ): Promise<boolean> {
        return await cartRequest("/delivery-payment", {
            method: "PUT",
            body: JSON.stringify({
                carrierRateUuid,
                carrierPaymentOptionUuid,
                pickupPointProvider: pickupPoint?.provider ?? null,
                pickupPointId: pickupPoint?.id ?? null,
                pickupPointName: pickupPoint?.name ?? null,
            }),
        }, storefrontMessage("ui.error.deliveryPaymentSave"));
    }

    return {
        siteName,
        pages,
        page,
        pageContext,
        shopBasicData,
        cartApiBasePath,
        customerApiBasePath,
        shopResolveApiPath,
        shopSearchApiPath,
        manufacturingProfileApiBasePath,
        packetaApiKey,
        LinkComponent,
        design,
        editorMode,
        storefrontLocationHref,
        storefrontPreview: onStorefrontNavigate !== undefined,
        headerMenuItems,
        mobileMenuItems,
        livePageContext,
        setLivePageContext,
        searchOpen,
        setSearchOpen,
        cartOpen,
        setCartOpen,
        mobileOpen,
        setMobileOpen,
        mobileTab,
        setMobileTab,
        previewSectionUuid,
        setPreviewSectionUuid,
        toastVisible,
        setToastVisible,
        favoriteProductIds,
        setFavoriteProductIds,
        favoriteProductPreviewMap,
        setFavoriteProductPreviewMap,
        recentlyViewedProducts,
        setRecentlyViewedProducts,
        cart,
        setCart,
        cartLoading,
        setCartLoading,
        cartError,
        setCartError,
        addingProductUuid,
        setAddingProductUuid,
        checkoutOptions,
        setCheckoutOptions,
        checkoutOptionsLoading,
        setCheckoutOptionsLoading,
        checkoutOptionsError,
        setCheckoutOptionsError,
        checkoutForm,
        setCheckoutForm,
        checkoutSubmitting,
        setCheckoutSubmitting,
        checkoutError,
        setCheckoutError,
        createdOrder,
        setCreatedOrder,
        customerSession,
        setCustomerSession,
        customerAddresses,
        setCustomerAddresses,
        customerLoading,
        setCustomerLoading,
        customerError,
        setCustomerError,
        catalogUpdating,
        setCatalogUpdating,
        browserLocation,
        setBrowserLocation,
        selectedLanguageCode,
        setSelectedLanguageCode,
        selectedCurrencyCode,
        setSelectedCurrencyCode,
        catalogAbortControllerRef,
        runtimeRootRef,
        customerUuid,
        supportedLanguages,
        supportedCurrencies,
        supportedLanguageKey,
        supportedCurrencyKey,
        defaultLanguage,
        contentLanguage,
        storefrontLanguageCode,
        storefrontMessage,
        storefrontHref,
        currentLanguage,
        defaultCurrency,
        currentCurrency,
        storefrontFormatting,
        displayName,
        cartFlow,
        homeHref,
        checkoutBaseHref,
        cartPageHref,
        cartHref,
        checkoutHref,
        checkoutContactHref,
        checkoutDoneHref,
        searchHref,
        storesHref,
        accountHref,
        orderApiBasePath,
        currentPagePath,
        currentBrowserPath,
        currentBrowserSearch,
        currentBrowserHash,
        languageOptions,
        currencyOptions,
        activeMenuUuids,
        activeSection,
        activeSectionItems,
        storeCities,
        contextFavoritePreviews,
        currentProductPreview,
        favoriteProducts,
        recommendedProductPreviews,
        visibleFavoriteProductIds,
        visibleFavoriteProducts,
        rawPageContentBlocks,
        pageContentBlocks,
        localizedPageContext,
        animationRefreshKey,
        pageContentVersion,
        normalizedPageContentVersion,
        promoBarBlockIndex,
        promoBarBlock,
        headerBlockIndex,
        headerBlock,
        hasFooterBlock,
        showLegacyPromoBarFallback,
        showLegacyHeaderFallback,
        showLegacyFooterFallback,
        shopSiteInfo,
        checkoutRouteStep,
        setCheckoutRouteStep,
        navigateCheckoutRouteStep,
        navigateCatalog,
        openSearch,
        openCart,
        closeOverlay,
        selectStorefrontLanguage,
        handleNewsletterSubmit,
        toggleFavorite,
        updateCheckoutForm,
        customerRequest,
        loginCustomer,
        registerCustomer,
        updateCustomerProfile,
        changeCustomerPassword,
        requestCustomerPasswordReset,
        confirmCustomerPasswordReset,
        reorderCustomerOrder,
        logoutCustomer,
        nullableText,
        submitCheckout,
        refreshCartAfterError,
        cartRequest,
        updateCartItem,
        removeCartItem,
        addCartItem,
        selectDeliveryPayment,
    };
}

export type ShopStorefrontRuntimeModel = ReturnType<typeof useShopStorefrontRuntime>;

const ShopStorefrontRuntimeContext = createContext<ShopStorefrontRuntimeModel | null>(null);

export function useShopStorefrontRuntimeContext(): ShopStorefrontRuntimeModel {
    const runtime = useContext(ShopStorefrontRuntimeContext);

    if (runtime === null) {
        throw new Error("Shop storefront runtime context is unavailable.");
    }

    return runtime;
}

export function ShopStorefrontRuntime({
    children,
    fallbackSiteName,
    previewAdapter,
    ...props
}: ShopStorefrontRuntimeProps & {
    children: (model: ShopStorefrontRuntimeModel) => ReactNode;
    fallbackSiteName: string;
    previewAdapter: ShopStorefrontPreviewAdapter;
}) {
    const model = useShopStorefrontRuntime({ ...props, fallbackSiteName, previewAdapter });

    return (
        <ShopManufacturingApiProvider
            cartApiBasePath={model.cartApiBasePath}
            manufacturingProfileApiBasePath={model.manufacturingProfileApiBasePath}
        >
            <ShopStorefrontRuntimeContext.Provider value={model}>
                <ShopStorefrontLinkContext.Provider value={model.LinkComponent}>
                    <span
                        aria-hidden="true"
                        data-shop-storefront-runtime-root="true"
                        hidden
                        ref={model.runtimeRootRef}
                    />
                    {children(model)}
                </ShopStorefrontLinkContext.Provider>
            </ShopStorefrontRuntimeContext.Provider>
        </ShopManufacturingApiProvider>
    );
}
